import { ref, computed } from "vue";
import { useStore } from "vuex";
import { LotInterface } from "@/types/LotInterface";
import { StageInterface } from "@/types/StageInterface";
import { EstateInterface } from "@/types/EstateInterface";
import { InitialHoldInterface } from "@/types/InitialHoldInterface";
import { StatusFiltersValues } from "@/constants/statusFilters";
import { LotStatus } from "@/enums/LotStatus";

export interface ExtendedLot extends LotInterface {
  exchangedDate?: string;
  [key: string]: any;
}

export interface LotListItem {
  id: string;
  selected: boolean;
  status: LotStatus;
}

export function useLotsData() {
  const store = useStore();

  const stage = ref<StageInterface | null>(null);
  const lots = ref<ExtendedLot[]>([]);
  const lotList = ref<LotListItem[]>([]);
  const initialHolds = ref<InitialHoldInterface[] | null>(null);
  const estate = ref<EstateInterface | null>(null);
  const activeLoaders = ref(1);
  const hasInitiallyLoaded = ref(false);

  const isLoading = computed(() => activeLoaders.value > 0);
  const estateId = computed(() => store.state.user.selectedEstateId);
  const userId = computed(() => store.state.user.data._id);

  const getStage = async (stageId: string) => {
    try {
      const res = await store.dispatch("stage/getStage", stageId);

      stage.value = res.data;
      return res.data;
    } catch (error) {
      console.error("Error fetching stage:", error);
      throw error;
    }
  };

  const getEstate = async () => {
    activeLoaders.value++;

    try {
      const res = await store.dispatch("estate/getEstate", estateId.value);

      estate.value = res.data;
      return res.data;
    } catch (error) {
      console.error("Error fetching estate:", error);
      estate.value = null;
      throw error;
    } finally {
      activeLoaders.value--;
    }
  };

  const getLots = async (
    stageId: string,
    filterValue: string,
    filterLabel: string
  ) => {
    activeLoaders.value++;

    try {
      const res = await store.dispatch("lot/getLots", {
        stageId,
        status: filterValue === StatusFiltersValues.ANY ? "" : filterLabel,
        eoi: true,
      });

      lots.value = res.data;
      lotList.value = lots.value.map((item) => ({
        id: item._id,
        selected: false,
        status: item.status,
      }));
      return res.data;
    } catch (error) {
      console.error("Error fetching lots:", error);
      throw error;
    } finally {
      activeLoaders.value--;
    }
  };

  const getInitialHolds = async () => {
    const lotIds = lotList.value.map((lot) => lot.id);

    if (lotIds.length === 0) return;

    activeLoaders.value++;

    try {
      const res = await store.dispatch(
        "initialHold/getApprovedInitialHoldsByLotsIds",
        {
          lotIds,
          estateId: estateId.value,
        }
      );

      initialHolds.value = res.data;
      return res.data;
    } catch (error) {
      console.error("Error fetching initial holds:", error);
      throw error;
    } finally {
      activeLoaders.value--;
    }
  };

  const updateUserProfile = () => {
    return store.dispatch("user/updateUserProfile", userId.value);
  };

  const initializeData = async (
    stageId: string,
    filterValue: string,
    filterLabel: string
  ) => {
    try {
      await getStage(stageId);
      await getEstate();
      await getLots(stageId, filterValue, filterLabel);
      await getInitialHolds();
      hasInitiallyLoaded.value = true;
    } finally {
      activeLoaders.value--;
    }
  };

  return {
    stage,
    lots,
    lotList,
    initialHolds,
    estate,
    isLoading,
    hasInitiallyLoaded,
    estateId,
    userId,
    getStage,
    getEstate,
    getLots,
    getInitialHolds,
    updateUserProfile,
    initializeData,
  };
}
